import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { SalonProvider, useSalon } from "./context/SalonContext";
import { auth } from "./lib/api";
import Sidebar from "./components/Sidebar";
import TotemScreensaver from "./components/TotemScreensaver";
import PinKeypad from "./components/PinKeypad";
import useIdleTimer from "./hooks/useIdleTimer";
import useFullscreen from "./hooks/useFullscreen";
import LandingLogin from "./modules/LandingLogin";
import OnboardingWizard from "./components/OnboardingWizard";
import SplashScreen from "./components/SplashScreen";
import PosTerminal from "./modules/PosTerminal";
import FinancialPanel from "./modules/FinancialPanel";
import Collaborators from "./modules/Collaborators";
import Clients from "./modules/Clients";
import Marketing from "./modules/Marketing";
import Agenda from "./modules/Agenda";
import EmployeeApp from "./modules/EmployeeApp";
import Account from "./modules/Account";
import Home from "./modules/Home";
import Avisos from "./modules/Avisos";
import Catalog from "./modules/Catalog";
import AdminApp from "./admin/AdminApp";
import PublicBooking from "./modules/PublicBooking";
import PublicRegister from "./modules/PublicRegister";
import AffiliatePortal from "./modules/AffiliatePortal";
import AffiliateSignup from "./modules/AffiliateSignup";
import LegalPlaceholder from "./modules/LegalPlaceholder";
import WhatsApp from "./modules/WhatsApp";
import Subscriptions from "./modules/Subscriptions";
import TrialBanner from "./components/TrialBanner";
import TermsGateModal from "./components/TermsGateModal";
import PaymentGate from "./components/PaymentGate";
import ProfessionalShell from "./modules/ProfessionalShell";
import { professionalAuth } from "./lib/professionalApi";

const MODULE_META = {
    home: { title: "Home", desc: "" },
    avisos: { title: "Avisos", desc: "Diário do dono · alertas do sistema · pendências." },
    pos: { title: "Terminal Balcão", desc: "PDV touchscreen para fechamento de comandas." },
    agenda: { title: "Agenda do Salão", desc: "Hub central event-driven · sincroniza Balcão e App do Colaborador em tempo real." },
    catalog: { title: "Catálogo & Estoque", desc: "Gerencie serviços, produtos, preços e estoque." },
    admin: { title: "Painel Financeiro", desc: "Acesso restrito do dono — KPIs e comissões." },
    employee: { title: "Gestão de Colaboradores", desc: "Cadastro, ganhos e histórico de comissões da equipe." },
    employeeapp: { title: "App do Colaborador", desc: "Simulador do app de bolso — agenda do dia e status em tempo real." },
    clients: { title: "Central de Clientes", desc: "Ficha completa, histórico de gastos e próximos agendamentos." },
    marketing: { title: "Hub de Marketing", desc: "Réguas de comunicação, campanhas WhatsApp e pesquisas de satisfação." },
    whatsapp: { title: "WhatsApp", desc: "Conexão, conversas, envios em lote e templates automáticos." },
    subscriptions: { title: "Clube de Assinatura", desc: "Planos recorrentes, assinantes e receita mensal." },
    account: { title: "Configurações", desc: "Dados cadastrais, identidade de marca, PINs de segurança e pagamento." },
};

// Modo Kiosk Imersivo: agora disponível em TODOS os módulos.
const KIOSK_ENABLED_MODULES = null; // null = sempre disponível

// Renderiza o módulo ativo — reutilizado tanto no shell "janela" quanto no shell "kiosk".
const ModuleRouter = ({ activeModule }) => (
    <>
        {activeModule === "pos" && <PosTerminal />}
        {activeModule === "catalog" && <Catalog />}
        {activeModule === "admin" && <FinancialPanel />}
        {activeModule === "employee" && <Collaborators />}
        {activeModule === "employeeapp" && <EmployeeApp />}
        {activeModule === "clients" && <Clients />}
        {activeModule === "marketing" && <Marketing />}
        {activeModule === "whatsapp" && <WhatsApp />}
        {activeModule === "subscriptions" && <Subscriptions />}
        {activeModule === "agenda" && <Agenda />}
        {activeModule === "home" && <Home />}
        {activeModule === "avisos" && <Avisos />}
        {activeModule === "account" && <Account />}
    </>
);

const TenantShell = () => {
    const { loggedIn, activeModule, setActiveModule, theme, brand, setLoggedIn } = useSalon();
    const { isIdle, reset } = useIdleTimer({ timeout: 45000, enabled: loggedIn });
    const { isFullscreen, enter, exit } = useFullscreen();
    const [showKioskIntro, setShowKioskIntro] = useState(false);
    // kioskLocked = sessão de kiosk ativa (independente do fullscreen do navegador,
    // pois ESC força saída do fullscreen mas a "sessão de turno" permanece travada
    // até que o gerente insira o PIN).
    const [kioskLocked, setKioskLocked] = useState(false);
    const [showExitPin, setShowExitPin] = useState(false);

    // Hide the persistent "Made with Emergent" badge + body scroll while in kiosk
    useEffect(() => {
        const badge = document.getElementById("emergent-badge");
        const showKiosk = isFullscreen && kioskLocked;
        if (badge) badge.style.display = showKiosk ? "none" : "";
        document.body.style.overflow = showKiosk ? "hidden" : "";
    }, [isFullscreen, kioskLocked]);

    // Se a sessão de kiosk está ativa, mantemos o módulo atual dentro do shell kiosk.
    // Não há mais restrição de módulos — o kiosk agora funciona em todo o app.

    // Sessão de COLABORADOR (login pessoal do profissional): shell própria,
    // enxuta, isolada do CRM do dono. Tem prioridade — mesmo sem `loggedIn`
    // (que é o estado do tenant/owner). owner/staff nunca têm este token.
    if (professionalAuth.getToken()) return <ProfessionalShell />;

    if (!loggedIn) return <LandingLogin />;

    // HARD GATE de cobrança: com pagamento pendente, NADA do CRM renderiza —
    // nem por URL direta (o backend também barra com 402). Free/Infinity nunca
    // caem aqui (o backend só marca access_blocked pra plano pago vencido).
    if (brand.paymentBlocked) return <PaymentGate />;

    const meta = MODULE_META[activeModule] || MODULE_META.studio;
    const tenantClass =
        theme.id === "vintage"
            ? "theme-vintage"
            : theme.id === "neon"
            ? "theme-neon"
            : "theme-organic";

    const kioskAvailable = KIOSK_ENABLED_MODULES === null || KIOSK_ENABLED_MODULES.has(activeModule);

    const handleEnterKiosk = async () => {
        try {
            await enter();
            setKioskLocked(true);
            toast.success("Modo Kiosk ativo", {
                description: "Saída protegida por PIN. ESC mostra a tela de desbloqueio.",
            });
        } catch (e) {
            toast.error("Não foi possível ativar tela cheia", {
                description: "Verifique permissões do navegador.",
            });
        }
    };

    const handleExitKiosk = () => {
        setShowExitPin(true);
    };

    const handleMobileLogout = () => {
        if (window.confirm("Sair do sistema?")) {
            auth.clear();
            setLoggedIn(false);
        }
    };

    const handlePinUnlock = async () => {
        setShowExitPin(false);
        setKioskLocked(false);
        if (isFullscreen) await exit();
        toast.success("Modo Kiosk encerrado");
    };

    // === KIOSK MODE LAYOUT ===
    // O kiosk é considerado "ativo" enquanto kioskLocked=true.
    // Se o usuário apertou ESC (fullscreen saiu, mas kioskLocked ainda é true),
    // mostramos uma tela de bloqueio exigindo PIN — segurança máxima.
    if (kioskLocked && kioskAvailable) {
        return (
            <div
                data-testid="kiosk-shell"
                className={`salon-shell crm-themed ${tenantClass} fixed inset-0 w-screen h-screen bg-slate-950 text-white flex overflow-hidden`}
            >
                {/* Sidebar fixa e sempre aberta durante Modo App — exceto no Terminal Balcão (PDV),
                    onde a experiência de operação exige tela cheia sem distrações. */}
                {activeModule !== "pos" && <Sidebar forceOpen />}

                {/* Main pane */}
                <div className="flex-1 min-w-0 flex flex-col relative">
                    {/* Kiosk header bar */}
                    <div
                        data-testid="kiosk-header"
                        className="shrink-0 h-14 flex items-center justify-between gap-3 px-5 border-b border-white/5 bg-slate-950/70 backdrop-blur-xl"
                    >
                        <div className="flex items-center gap-2 text-xs min-w-0">
                            <span
                                className="inline-flex h-2 w-2 rounded-full animate-pulse shrink-0"
                                style={{ background: brand.primaryColor }}
                            ></span>
                            <span className="font-semibold text-white truncate">{brand.shopName}</span>
                            <span className="text-slate-500">·</span>
                            <span className="text-slate-400 uppercase tracking-wider truncate">{meta.title}</span>
                            {!isFullscreen && (
                                <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] uppercase tracking-wider font-bold shrink-0">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    Trava ativa
                                </span>
                            )}
                        </div>
                        <button
                            data-testid="exit-kiosk-btn"
                            onClick={handleExitKiosk}
                            className="group flex items-center gap-2 min-h-[40px] px-4 rounded-full border border-white/10 hover:border-amber-500/60 hover:bg-slate-900 transition-all text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white shadow-lg"
                            title="Sair do Modo App (PIN protegido)"
                        >
                            <i className="fa-solid fa-lock text-amber-400 group-hover:scale-110 transition-transform"></i>
                            Sair (PIN)
                        </button>
                    </div>

                    {/* Full-bleed active module */}
                    <div className="flex-1 w-full px-4 lg:px-6 py-4 overflow-auto">
                        <ModuleRouter activeModule={activeModule} />
                    </div>
                </div>

                {/* Se o navegador saiu do fullscreen (ex: ESC) mas a sessão de kiosk
                    ainda está travada, mostramos a overlay forçando o PIN. */}
                {!isFullscreen && (
                    <div
                        data-testid="kiosk-locked-overlay"
                        className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4"
                    >
                        <PinKeypad
                            testId="kiosk-exit-keypad"
                            title="Modo Kiosk Bloqueado"
                            subtitle="Insira o PIN do gerente para encerrar o turno"
                            expectedPin={brand.kioskPin || "2580"}
                            primaryColor={brand.primaryColor}
                            onSuccess={handlePinUnlock}
                            secondaryActionLabel="Continuar operação"
                            onSecondaryAction={async () => {
                                try {
                                    await enter();
                                } catch (_) {
                                    toast.error("Permita tela cheia para continuar o Modo Kiosk.");
                                }
                            }}
                        />
                    </div>
                )}

                {/* PIN modal acionado pelo botão "Sair" (com fullscreen ainda ativo) */}
                {showExitPin && isFullscreen && (
                    <div
                        data-testid="kiosk-exit-overlay"
                        className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4"
                    >
                        <PinKeypad
                            testId="kiosk-exit-keypad"
                            title="Encerrar Modo Kiosk"
                            subtitle="Insira o PIN do gerente para sair"
                            expectedPin={brand.kioskPin || "2580"}
                            primaryColor={brand.primaryColor}
                            onSuccess={handlePinUnlock}
                            onCancel={() => setShowExitPin(false)}
                        />
                    </div>
                )}

                {isIdle && <TotemScreensaver onWake={reset} />}
                <TermsGateModal />
            </div>
        );
    }

    // === WINDOWED LAYOUT ===
    const needsOnboarding = !brand.onboardingComplete;
    return (
        <div className={`salon-shell crm-themed ${tenantClass} min-h-screen bg-slate-950 text-white flex`}>
            {needsOnboarding && <OnboardingWizard />}
            <Sidebar />
            <main className="flex-1 min-w-0 flex flex-col">
                <TrialBanner />
                <header className="px-5 lg:px-8 py-5 flex items-center justify-between border-b border-white/5 bg-slate-950/60 backdrop-blur-xl sticky top-0 z-30">
                    <div className="flex items-center gap-3 min-w-0">
                        {activeModule !== "home" && (
                            <button
                                data-testid="module-back-btn"
                                onClick={() => setActiveModule("home")}
                                aria-label="Voltar para a Home"
                                title="Voltar para a Home"
                                className="shrink-0 h-11 w-11 min-h-[44px] min-w-[44px] rounded-full border border-white/10 bg-slate-900/60 hover:bg-slate-900 hover:border-amber-500/40 text-slate-300 hover:text-amber-200 transition-all flex items-center justify-center"
                            >
                                <i className="fa-solid fa-arrow-left"></i>
                            </button>
                        )}
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 truncate">
                                {brand.shopName}
                            </div>
                            <h1 className="salon-heading text-2xl text-white mt-1 truncate">{meta.title}</h1>
                            <p className="text-xs text-slate-400 mt-0.5 truncate">{meta.desc}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="hidden md:flex items-center gap-3 text-xs text-slate-400">
                            {kioskAvailable && (
                                <button
                                    data-testid="enter-kiosk-btn"
                                    onClick={() => setShowKioskIntro(true)}
                                    className="min-h-[36px] inline-flex items-center gap-2 px-3.5 rounded-full border border-amber-500/35 bg-amber-500/8 hover:bg-amber-500/20 hover:border-amber-400/70 text-amber-200 hover:text-amber-100 transition-all uppercase tracking-[0.15em] text-[10px] font-bold"
                                    title="Ativar Modo App (tela cheia)"
                                >
                                    <i className="fa-solid fa-expand"></i>
                                    Modo App
                                </button>
                            )}
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/5 bg-slate-900/40">
                                <i className="fa-solid fa-circle text-emerald-400 text-[8px] animate-pulse"></i>
                                Online
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/5 bg-slate-900/40">
                                <i className="fa-solid fa-fingerprint"></i>
                                <span className="font-mono">{brand.shopId}</span>
                            </span>
                        </div>
                        {/* Sidebar (com o logout) fica oculta abaixo de 768px — este botão
                            é o único jeito de sair do sistema em mobile. */}
                        <button
                            data-testid="mobile-logout-btn"
                            onClick={handleMobileLogout}
                            aria-label="Sair do sistema"
                            title="Sair do sistema"
                            className="md:hidden shrink-0 h-11 w-11 min-h-[44px] min-w-[44px] rounded-full border border-white/10 bg-slate-900/60 hover:bg-red-500/10 hover:border-red-500/40 text-slate-300 hover:text-red-300 transition-all flex items-center justify-center"
                        >
                            <i className="fa-solid fa-right-from-bracket"></i>
                        </button>
                    </div>
                </header>

                <div className="flex-1 p-5 lg:p-7">
                    <ModuleRouter activeModule={activeModule} />
                </div>
            </main>

            {isIdle && <TotemScreensaver onWake={reset} />}
            <TermsGateModal />

            {/* Kiosk intro modal — explains the mode then triggers fullscreen on click */}
            {showKioskIntro && (
                <KioskIntroModal
                    moduleTitle={meta.title}
                    primaryColor={brand.primaryColor}
                    onClose={() => setShowKioskIntro(false)}
                    onConfirm={async () => {
                        setShowKioskIntro(false);
                        await handleEnterKiosk();
                    }}
                />
            )}
        </div>
    );
};

const KioskIntroModal = ({ moduleTitle, primaryColor, onClose, onConfirm }) => (
    <div
        data-testid="kiosk-intro-modal"
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-slate-950 shadow-2xl overflow-hidden"
        >
            <div
                className="absolute -inset-px rounded-3xl opacity-50 blur-2xl pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}33)` }}
            ></div>
            <div className="relative p-8">
                <div className="flex items-center justify-between mb-5">
                    <div
                        className="h-14 w-14 rounded-2xl flex items-center justify-center"
                        style={{ background: `${primaryColor}22`, color: primaryColor }}
                    >
                        <i className="fa-solid fa-expand text-2xl"></i>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-10 w-10 min-h-[44px] min-w-[44px] rounded-full bg-white/5 hover:bg-white/10 text-white transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Modo Kiosk Imersivo
                </div>
                <h2 className="salon-heading text-3xl text-white mt-1">Ativar Modo App</h2>
                <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                    O <span className="text-white font-semibold">{moduleTitle}</span> entrará em
                    modo tela cheia, ocultando barras do navegador e do sistema. Ideal para o
                    expediente — o operador foca 100% no terminal.
                </p>

                <ul className="mt-5 space-y-2.5">
                    {[
                        { i: "fa-eye-slash", t: "Esconde a barra do navegador e a barra de tarefas" },
                        { i: "fa-shield-halved", t: "Trava saída do PDV: somente quem tem o PIN encerra o turno" },
                        { i: "fa-crown", t: "Feature Premium · disponível na sua assinatura" },
                    ].map((b) => (
                        <li key={b.i} className="flex items-start gap-3 text-sm text-slate-200">
                            <span
                                className="mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
                                style={{ background: `${primaryColor}22`, color: primaryColor }}
                            >
                                <i className={`fa-solid ${b.i} text-xs`}></i>
                            </span>
                            {b.t}
                        </li>
                    ))}
                </ul>

                <div className="mt-7 flex items-center gap-3">
                    <button
                        data-testid="kiosk-intro-cancel"
                        onClick={onClose}
                        className="min-h-[48px] px-5 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-sm transition-all"
                    >
                        Cancelar
                    </button>
                    <button
                        data-testid="kiosk-intro-confirm"
                        onClick={onConfirm}
                        className="flex-1 min-h-[52px] rounded-xl text-slate-950 font-bold uppercase tracking-[0.15em] text-sm transition-all shadow-lg"
                        style={{
                            background: primaryColor,
                            boxShadow: `0 10px 30px -10px ${primaryColor}80`,
                        }}
                    >
                        <i className="fa-solid fa-expand mr-2"></i>Entrar no Modo App
                    </button>
                </div>
            </div>
        </div>
    </div>
);

function App() {
    const [splashDone, setSplashDone] = useState(() => {
        // Show splash only once per browser session
        return sessionStorage.getItem("kinng.splash.shown") === "1";
    });

    return (
        <SalonProvider>
            {!splashDone && (
                <SplashScreen
                    onComplete={() => {
                        sessionStorage.setItem("kinng.splash.shown", "1");
                        setSplashDone(true);
                    }}
                />
            )}
            <BrowserRouter>
                <Routes>
                    <Route path="/admin" element={<AdminApp />} />
                    <Route path="/book/:shopId" element={<PublicBooking />} />
                    <Route path="/register" element={<PublicRegister />} />
                    <Route path="/cadastro" element={<PublicRegister />} />
                    <Route path="/afiliado/cadastro" element={<AffiliateSignup />} />
                    <Route path="/afiliado" element={<AffiliatePortal />} />
                    <Route path="/termos" element={<LegalPlaceholder title="Termos de Uso" endpoint="/public/terms" />} />
                    <Route path="/privacidade" element={<LegalPlaceholder title="Política de Privacidade" endpoint="/public/privacy" />} />
                    <Route path="/*" element={<TenantShell />} />
                </Routes>
            </BrowserRouter>
            <Toaster
                position="top-right"
                theme="dark"
                richColors
                toastOptions={{
                    style: {
                        background: "rgba(15, 23, 42, 0.95)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        backdropFilter: "blur(20px)",
                    },
                }}
            />
        </SalonProvider>
    );
}

export default App;
